/**
 * SidebarUserFooter — the user account card that lives at the bottom of
 * the side nav on desktop and the bottom of the mobile drawer.
 *
 * Renders an avatar + display name + email + chevron (or just the avatar
 * when `collapsed`). Clicking it opens a `Dropdown` menu with Settings,
 * Organizations, Theme toggle, Help, an internal Debug shortcut, and Log out.
 *
 * Used by the main `SideNavigation` (expanded and collapsed) and the settings
 * sidebar so every surface renders the same menu. The menu is the shared
 * `Dropdown` primitive (react-aria under the hood), so keyboard navigation,
 * Escape-to-close, focus return, outside-click dismissal and ARIA roles all
 * come from the design system rather than hand-written effects.
 */

import {
  Building07,
  ChevronSelectorVertical,
  HelpCircle,
  LogOut01,
  Moon01,
  Settings01,
  Sun,
  Terminal,
} from '@untitledui/icons';
import { useCallback, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { HelpModal } from '@/components/help/HelpModal';
import { useOrg } from '@/contexts/OrgContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { DiagnosticsReportModal } from '@/lib/diagnostics/DiagnosticsReportModal';
import type { ProfileData } from '@/lib/profile/types';

type UserProfile = ProfileData['user'];

/** Matches the destructive-row convention used by other Dropdown menus. */
const DESTRUCTIVE_ITEM_CLASS = '[&_svg]:text-error-600 [&_span]:text-error-600';

function getUserInitials(profile: UserProfile | undefined): string {
  if (!profile) {
    return 'U';
  }
  const { first_name, last_name, email } = profile;
  if (first_name && last_name) {
    return `${first_name[0]}${last_name[0]}`.toUpperCase();
  }
  if (first_name) {
    return first_name[0].toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'U';
}

interface SidebarUserFooterProps {
  /** Icon-only trigger for the collapsed side nav; the menu is identical. */
  collapsed?: boolean;
}

export function SidebarUserFooter({
  collapsed = false,
}: SidebarUserFooterProps) {
  const { orgId } = useParams();
  const { currentOrg } = useOrg();
  const { setTheme, isDark } = useTheme();
  const isInternalUser = useIsInternalUser();
  const { data: profileData } = useUserProfileData();
  const userProfile = profileData?.user;

  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const handleToggleTheme = useCallback(() => {
    setTheme(isDark ? 'light' : 'dark');
  }, [isDark, setTheme]);

  const handleOpenHelp = useCallback(() => {
    setHelpModalOpen(true);
  }, []);

  const handleCloseHelp = useCallback(() => {
    setHelpModalOpen(false);
  }, []);

  const handleOpenReport = useCallback(() => {
    setReportOpen(true);
    setHelpModalOpen(false);
  }, []);

  const handleCloseReport = useCallback(() => {
    setReportOpen(false);
  }, []);

  const displayName = userProfile?.get_display_name || 'User';
  const initials = getUserInitials(userProfile);

  return (
    <div className='relative'>
      <Dropdown.Root instance='sidebar-user-menu'>
        {collapsed ? (
          <AriaButton
            aria-label='Open user menu'
            className='group relative inline-flex cursor-pointer rounded-full outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2'
          >
            <Avatar
              size='md'
              src={userProfile?.avatar_url}
              initials={initials}
              alt={displayName}
            />
          </AriaButton>
        ) : (
          <AriaButton className='relative w-full flex items-center gap-[7px] rounded-xl p-2 ring-1 ring-secondary ring-inset hover:bg-primary_hover transition-colors cursor-pointer outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2'>
            <Avatar
              size='xs'
              src={userProfile?.avatar_url}
              initials={initials}
              alt={displayName}
            />
            <div className='flex-1 min-w-0 text-left'>
              <div className='text-[13px] font-semibold text-primary truncate leading-tight'>
                {userProfile?.get_display_name ||
                  userProfile?.first_name ||
                  'User'}
              </div>
              <div className='text-xs text-tertiary truncate'>
                {userProfile?.email || ''}
              </div>
            </div>
            <ChevronSelectorVertical className='size-4 shrink-0 text-fg-quaternary self-start mt-0.5' />
          </AriaButton>
        )}

        <Dropdown.Popover placement='top start' className='w-56'>
          <div className='px-3 py-2 border-b border-border-secondary'>
            <div className='text-sm font-medium text-primary truncate'>
              {displayName}
            </div>
            <div className='text-xs text-tertiary truncate'>
              {currentOrg?.name || 'No team'}
            </div>
          </div>
          {/* Rows are actions/links, not radio selections — override the
              Dropdown.Menu default of selectionMode='single'. */}
          <Dropdown.Menu selectionMode='none'>
            {orgId && (
              <Dropdown.Item
                id='settings'
                href={`/${orgId}/profile`}
                label='Settings'
                icon={Settings01}
              />
            )}
            <Dropdown.Item
              id='organizations'
              href='/organizations'
              label='Organizations'
              icon={Building07}
            />
            <Dropdown.Separator />
            <Dropdown.Item
              id='theme'
              onAction={handleToggleTheme}
              label='Theme'
              icon={isDark ? Moon01 : Sun}
            />
            <Dropdown.Item
              id='help'
              onAction={handleOpenHelp}
              label='Help'
              icon={HelpCircle}
            />
            {isInternalUser && orgId && (
              <>
                <Dropdown.Separator />
                <Dropdown.Item
                  id='debug'
                  href={`/${orgId}/debug`}
                  label='Debug'
                  icon={Terminal}
                />
              </>
            )}
            <Dropdown.Separator />
            <Dropdown.Item
              id='logout'
              href='/account/logout'
              label='Log out'
              icon={LogOut01}
              className={DESTRUCTIVE_ITEM_CLASS}
            />
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>
      <HelpModal
        isOpen={helpModalOpen}
        onClose={handleCloseHelp}
        slackChannelUrl={currentOrg?.slackChannelUrl}
        onReportProblem={handleOpenReport}
      />
      <DiagnosticsReportModal
        isOpen={reportOpen}
        onClose={handleCloseReport}
        trigger='help_menu'
      />
    </div>
  );
}
